/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useState } from "react";
import { cn } from "./cn";

type SpinnerSize = "xs" | "sm" | "md" | "lg";

interface SpinnerProps {
  size?: SpinnerSize;
  className?: string;
}

const sizes: Record<SpinnerSize, { size: string; border: string }> = {
  xs: { size: "14px", border: "2px" },
  sm: { size: "18px", border: "2px" },
  md: { size: "24px", border: "3px" },
  lg: { size: "44px", border: "4px" }
};

const ROTATION_MS = 1600;

/*
 * A page often shows several loaders in a row (route, page, lazy chunk), each a new element whose
 * animation would restart from zero and read as a stutter. Starting every spinner at the phase the
 * clock dictates makes consecutive loaders one continuous rotation.
 */
function clockPhase(): string {
  return `-${Date.now() % ROTATION_MS}ms`;
}

export function Spinner({ size = "md", className }: SpinnerProps) {
  const { size: px, border } = sizes[size];
  const [phase] = useState(clockPhase);
  return (
    <span
      role="status"
      aria-label="Loading"
      style={{ "--spinner-size": px, "--spinner-border": border, "--spinner-phase": phase } as React.CSSProperties}
      className={cn("loader-ring text-accent", className)}
    />
  );
}
